import { CustomFieldType, type CustomFieldDefinition } from "@/types/crm";

export interface DealTypeTemplate {
  name: string;
  description: string;
  customFields: CustomFieldDefinition[];
}

export interface IndustryTemplate {
  id: string;
  name: string;
  description: string;
  icon: string;
  dealTypes: DealTypeTemplate[];
}

export const industryTemplates: IndustryTemplate[] = [
  {
    id: "real-estate",
    name: "Real Estate",
    description: "Property sales, rentals, and commercial leasing",
    icon: "Building2",
    dealTypes: [
      {
        name: "Property Sale",
        description: "Residential or commercial property sale",
        customFields: [
          { name: "Price", type: CustomFieldType.Number, required: true },
          {
            name: "Property Type",
            type: CustomFieldType.Select,
            required: true,
            options: ["House", "Apartment", "Commercial", "Land"],
          },
          { name: "Address", type: CustomFieldType.Text, required: false },
          {
            name: "Square Footage",
            type: CustomFieldType.Number,
            required: false,
          },
          { name: "Bedrooms", type: CustomFieldType.Number, required: false },
        ],
      },
      {
        name: "Property Rental",
        description: "Residential or commercial property rental",
        customFields: [
          {
            name: "Monthly Rent",
            type: CustomFieldType.Number,
            required: true,
          },
          {
            name: "Lease Term",
            type: CustomFieldType.Select,
            required: false,
            options: ["6 Months", "12 Months", "24 Months"],
          },
          { name: "Address", type: CustomFieldType.Text, required: false },
          {
            name: "Available Date",
            type: CustomFieldType.Date,
            required: false,
          },
        ],
      },
      {
        name: "Commercial Lease",
        description: "Commercial property lease agreement",
        customFields: [
          {
            name: "Annual Rent",
            type: CustomFieldType.Number,
            required: true,
          },
          {
            name: "Square Footage",
            type: CustomFieldType.Number,
            required: false,
          },
          { name: "Lease Term", type: CustomFieldType.Text, required: false },
          {
            name: "Property Class",
            type: CustomFieldType.Select,
            required: false,
            options: ["A", "B", "C"],
          },
        ],
      },
    ],
  },
  {
    id: "saas",
    name: "SaaS / Software",
    description: "Software subscriptions, upsells, and renewals",
    icon: "Monitor",
    dealTypes: [
      {
        name: "New Business",
        description: "New customer acquisition",
        customFields: [
          { name: "MRR", type: CustomFieldType.Number, required: true },
          {
            name: "Plan Tier",
            type: CustomFieldType.Select,
            required: true,
            options: ["Starter", "Pro", "Enterprise"],
          },
          { name: "Seats", type: CustomFieldType.Number, required: false },
          {
            name: "Trial End Date",
            type: CustomFieldType.Date,
            required: false,
          },
        ],
      },
      {
        name: "Upsell",
        description: "Existing customer plan upgrade",
        customFields: [
          {
            name: "Current MRR",
            type: CustomFieldType.Number,
            required: false,
          },
          { name: "New MRR", type: CustomFieldType.Number, required: true },
          {
            name: "Upgrade Reason",
            type: CustomFieldType.Text,
            required: false,
          },
        ],
      },
      {
        name: "Renewal",
        description: "Existing customer contract renewal",
        customFields: [
          {
            name: "Annual Value",
            type: CustomFieldType.Number,
            required: true,
          },
          {
            name: "Contract End Date",
            type: CustomFieldType.Date,
            required: true,
          },
          {
            name: "Renewal Risk",
            type: CustomFieldType.Select,
            required: false,
            options: ["Low", "Medium", "High"],
          },
        ],
      },
    ],
  },
  {
    id: "consulting",
    name: "Consulting / Services",
    description: "Project proposals, retainers, and advisory",
    icon: "Briefcase",
    dealTypes: [
      {
        name: "Project Proposal",
        description: "Client project proposal and scoping",
        customFields: [
          { name: "Budget", type: CustomFieldType.Number, required: true },
          {
            name: "Estimated Hours",
            type: CustomFieldType.Number,
            required: false,
          },
          { name: "Start Date", type: CustomFieldType.Date, required: false },
          {
            name: "Service Type",
            type: CustomFieldType.Select,
            required: false,
            options: ["Strategy", "Implementation", "Audit", "Training"],
          },
        ],
      },
      {
        name: "Retainer",
        description: "Ongoing monthly retainer agreement",
        customFields: [
          {
            name: "Monthly Fee",
            type: CustomFieldType.Number,
            required: true,
          },
          { name: "Scope", type: CustomFieldType.Text, required: false },
          {
            name: "Contract Term",
            type: CustomFieldType.Select,
            required: false,
            options: ["3 Months", "6 Months", "12 Months"],
          },
        ],
      },
    ],
  },
  {
    id: "marketing",
    name: "Marketing / Agency",
    description: "Campaigns, client retainers, and creative projects",
    icon: "Megaphone",
    dealTypes: [
      {
        name: "Campaign",
        description: "Marketing campaign for a client",
        customFields: [
          { name: "Budget", type: CustomFieldType.Number, required: true },
          {
            name: "Channel",
            type: CustomFieldType.Select,
            required: false,
            options: ["Digital", "Print", "Social", "TV", "Radio"],
          },
          { name: "Start Date", type: CustomFieldType.Date, required: false },
          { name: "KPI Target", type: CustomFieldType.Text, required: false },
        ],
      },
      {
        name: "Retainer",
        description: "Ongoing client retainer for services",
        customFields: [
          {
            name: "Monthly Fee",
            type: CustomFieldType.Number,
            required: true,
          },
          { name: "Services", type: CustomFieldType.Text, required: false },
          {
            name: "Contract Term",
            type: CustomFieldType.Select,
            required: false,
            options: ["3 Months", "6 Months", "12 Months"],
          },
        ],
      },
      {
        name: "Project",
        description: "One-off creative or marketing project",
        customFields: [
          { name: "Budget", type: CustomFieldType.Number, required: true },
          { name: "Deadline", type: CustomFieldType.Date, required: false },
          {
            name: "Deliverables",
            type: CustomFieldType.Text,
            required: false,
          },
        ],
      },
    ],
  },
  {
    id: "construction",
    name: "Construction",
    description: "Bids, project contracts, and subcontracting",
    icon: "HardHat",
    dealTypes: [
      {
        name: "Bid / Estimate",
        description: "Project bid or cost estimate",
        customFields: [
          {
            name: "Estimated Cost",
            type: CustomFieldType.Number,
            required: true,
          },
          {
            name: "Site Address",
            type: CustomFieldType.Text,
            required: true,
          },
          { name: "Start Date", type: CustomFieldType.Date, required: false },
          {
            name: "Project Type",
            type: CustomFieldType.Select,
            required: false,
            options: [
              "Residential",
              "Commercial",
              "Industrial",
              "Infrastructure",
            ],
          },
        ],
      },
      {
        name: "Project Contract",
        description: "Signed construction project contract",
        customFields: [
          {
            name: "Contract Value",
            type: CustomFieldType.Number,
            required: true,
          },
          {
            name: "Completion Date",
            type: CustomFieldType.Date,
            required: false,
          },
          { name: "Phases", type: CustomFieldType.Number, required: false },
        ],
      },
      {
        name: "Subcontract",
        description: "Subcontractor agreement",
        customFields: [
          {
            name: "Subcontractor",
            type: CustomFieldType.Text,
            required: true,
          },
          { name: "Scope", type: CustomFieldType.Text, required: false },
          { name: "Value", type: CustomFieldType.Number, required: true },
        ],
      },
    ],
  },
  {
    id: "ecommerce",
    name: "E-commerce / Retail",
    description: "Wholesale orders, partnerships, and supplier deals",
    icon: "ShoppingCart",
    dealTypes: [
      {
        name: "Wholesale Order",
        description: "Bulk order from wholesale buyer",
        customFields: [
          {
            name: "Order Value",
            type: CustomFieldType.Number,
            required: true,
          },
          { name: "Quantity", type: CustomFieldType.Number, required: false },
          {
            name: "Delivery Date",
            type: CustomFieldType.Date,
            required: false,
          },
          {
            name: "Payment Terms",
            type: CustomFieldType.Select,
            required: false,
            options: ["Net 15", "Net 30", "Net 60", "COD"],
          },
        ],
      },
      {
        name: "Partnership",
        description: "Business partnership or reseller agreement",
        customFields: [
          {
            name: "Partner Type",
            type: CustomFieldType.Select,
            required: false,
            options: ["Reseller", "Affiliate", "Distributor"],
          },
          {
            name: "Revenue Share %",
            type: CustomFieldType.Number,
            required: false,
          },
          { name: "Territory", type: CustomFieldType.Text, required: false },
        ],
      },
      {
        name: "Supplier Deal",
        description: "Supplier contract or negotiation",
        customFields: [
          {
            name: "Product Category",
            type: CustomFieldType.Text,
            required: true,
          },
          { name: "Volume", type: CustomFieldType.Number, required: false },
          { name: "Unit Cost", type: CustomFieldType.Number, required: true },
        ],
      },
    ],
  },
];
